import { RoundedButton } from '@/components/rounded-button';
import {
  DialogActionTrigger,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
} from '@/components/ui/dialog';
import { InputGroup } from '@/components/ui/input-group';
import { dailySpinChain, dailySpinChainId } from '@/constants/shared';
import { useDailySpinOnchain } from '@/hooks/useDailySpinOnchain';
import { RAFFLE_ABI } from '@/network/abi/raffle';
import { extractPositiveNumber, formatNumberToLocale } from '@/utils';
import {
  Box,
  Field,
  FormatNumber,
  Group,
  Heading,
  HStack,
  Image,
  Input,
  Text,
  useUpdateEffect,
} from '@chakra-ui/react';
import { useAppKitNetwork } from '@reown/appkit/react';
import { ChangeEvent, useCallback, useState } from 'react';
import { useWriteContract } from 'wagmi';
import { TicketsAddedModalContent } from './TicketsAddedModalContent';

export const AddTicketsModal = ({
  name,
  prizeId,
  ticketsAvailable,
  numOfTicketsToSubmit,
  setNumOfTicketsToSubmit,
  onSuccess,
  fullWidth = false,
}: {
  name: string;
  prizeId: number;
  ticketsAvailable: number;
  numOfTicketsToSubmit: number;
  setNumOfTicketsToSubmit: (n: number) => void;
  onSuccess?: () => void;
  fullWidth?: boolean;
}) => {
  const [showTicketsAdded, setShowTicketsAdded] = useState(0);
  const [open, setOpen] = useState(false);
  const { switchNetwork } = useAppKitNetwork();
  const { refetchDailySpinStats } = useDailySpinOnchain();

  const { writeContractAsync } = useWriteContract();
  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const validatedAmount = Number(extractPositiveNumber(e.target.value));
    setNumOfTicketsToSubmit(Math.min(validatedAmount, ticketsAvailable));
  };
  const onSubmit = useCallback(async () => {
    const contractPrizeId = prizeId + 1;

    try {
      await switchNetwork(dailySpinChain);
      await writeContractAsync({
        abi: RAFFLE_ABI,
        address: import.meta.env.VITE_RAFFLE_CONTRACT_ADDRESS,
        functionName: 'spendRaffle',
        chainId: dailySpinChainId,
        args: [BigInt(contractPrizeId), BigInt(numOfTicketsToSubmit)],
      });

      setShowTicketsAdded(numOfTicketsToSubmit);
      // Reset the input value after successful submission
      setNumOfTicketsToSubmit(0);

      // Immediately refetch daily spin stats
      refetchDailySpinStats();

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('[AddTickets] Error adding tickets:', error);
    }
  }, [
    numOfTicketsToSubmit,
    prizeId,
    writeContractAsync,
    onSuccess,
    setNumOfTicketsToSubmit,
    switchNetwork,
    refetchDailySpinStats,
  ]);

  useUpdateEffect(() => {
    if (!open) {
      setNumOfTicketsToSubmit(0);
    }
  }, [open]);

  return (
    <DialogRoot size="md" lazyMount open={open} onOpenChange={(e) => setOpen(e.open)}>
      <DialogTrigger asChild>
        <RoundedButton bgColor="bg" color="fg" variant="surface" w={fullWidth ? 'full' : 'auto'}>
          Add Tickets
        </RoundedButton>
      </DialogTrigger>
      {showTicketsAdded > 0 ? (
        <TicketsAddedModalContent
          ticketsUsed={showTicketsAdded}
          prize={name}
          onClose={() => {
            setShowTicketsAdded(0);
            setOpen(false);
          }}
        />
      ) : (
        <DialogContent spaceY={2}>
          <DialogHeader display="unset" textAlign="center" spaceY={2} fontFamily="Lufga, sans-serif">
            <Heading size="4xl">Add Tickets</Heading>
            <DialogDescription fontWeight={500} fontSize="lg">
              {name}
            </DialogDescription>
          </DialogHeader>
          <DialogBody spaceY={2}>
            <Field.Root borderColor="border" borderWidth="thin" borderRadius="xl" p={5} mb={4}>
              <InputGroup
                w="full"
                endElement={<Image src="/assets/daily-spin/raffle-ticket-reward.svg" />}
                endElementProps={{ p: 0 }}
              >
                <Input
                  border="none"
                  fontWeight="500"
                  placeholder="0"
                  value={numOfTicketsToSubmit}
                  onChange={onChange}
                  px={0}
                  fontSize="4xl"
                  outline="none"
                />
              </InputGroup>
              <Field.HelperText w="full" mt={8}>
                <HStack justifyContent="space-between">
                  <Text>{formatNumberToLocale(ticketsAvailable)} Tickets Available</Text>
                  <Group>
                    {[0.25, 0.5, 1].map((percent) => (
                      <SetNumTicketsButton
                        key={percent}
                        percentToSet={percent}
                        setNumOfTicketsToSubmit={setNumOfTicketsToSubmit}
                        ticketsAvailable={ticketsAvailable}
                      />
                    ))}
                  </Group>
                </HStack>
              </Field.HelperText>
            </Field.Root>
            <RoundedButton w="full" onClick={onSubmit} disabled={!numOfTicketsToSubmit}>
              Add Tickets
            </RoundedButton>
            <DialogActionTrigger asChild>
              <RoundedButton w="full" variant="outline">
                Cancel
              </RoundedButton>
            </DialogActionTrigger>
            <Box>
              <Text fontSize="xs" color="text.tertiary" textAlign="center" lineHeight="1.4" mt={4}>
                Prize availability and geo restrictions apply; winners will be compensated in stablecoin equivalent when
                physical prizes cannot be acquired in a timely manner or shipped directly.
              </Text>
            </Box>
          </DialogBody>
        </DialogContent>
      )}
    </DialogRoot>
  );
};

const SetNumTicketsButton = ({
  percentToSet,
  setNumOfTicketsToSubmit,
  ticketsAvailable,
}: {
  percentToSet: number;
  ticketsAvailable: number;
  setNumOfTicketsToSubmit: (n: number) => void;
}) => {
  const onClick = useCallback(() => {
    setNumOfTicketsToSubmit(Math.floor(ticketsAvailable * percentToSet));
  }, [percentToSet, setNumOfTicketsToSubmit, ticketsAvailable]);
  return (
    <RoundedButton h={26} color="fg.muted" bgColor="bg.subtle" onClick={onClick}>
      {percentToSet === 1 ? 'MAX' : <FormatNumber value={percentToSet} style="percent" />}
    </RoundedButton>
  );
};
